import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Wrench, Globe, FileText, Code, Smartphone, Shield, Zap, Languages, Search,
  Loader2, CheckCircle, XCircle, AlertTriangle, ArrowRight, BarChart3, Cpu,
  Accessibility, Cookie, Lock, Link2, GitCompareArrows, Bot, MousePointerClick,
  ChevronRight, Sparkles, BookOpen,
} from 'lucide-react'
import api from '../lib/api'
import { isValidUrl } from '../lib/url'
import ScoreGauge from '../components/ScoreGauge'
import { extractFindings } from '../lib/reportPdf'
import { useSeoHead, SITE } from '../lib/seo'
import ShareBar from '../components/ShareBar'
import ToolRating, { type RatingSummary } from '../components/ToolRating'
import { resolveToolSlug, toolPath, helpPath } from '../lib/localizedUrl'
import { getToolPage, TOOL_PAGES, toolH1, type ToolPage } from '../data/toolPages'
import tpEn from '../data/toolPages.en.json'
import tpDe from '../data/toolPages.de.json'
import tpPl from '../data/toolPages.pl.json'
import tpSk from '../data/toolPages.sk.json'
import tpIt from '../data/toolPages.it.json'
import tpHu from '../data/toolPages.hu.json'
import tpUk from '../data/toolPages.uk.json'
import tpEs from '../data/toolPages.es.json'
import tpFr from '../data/toolPages.fr.json'
import tpNl from '../data/toolPages.nl.json'
import tpJa from '../data/toolPages.ja.json'
import NotFoundPage from './NotFoundPage'

// Překlady obsahu — slug/toolId/inputType/related/kb zůstávají z české předlohy.
const TRANS: Record<string, any[]> = { en: tpEn, de: tpDe, pl: tpPl, sk: tpSk, it: tpIt, hu: tpHu, uk: tpUk, es: tpEs, fr: tpFr, nl: tpNl, ja: tpJa }

const ICONS: Record<string, any> = {
  robots: FileText, sitemap: Globe, schema: Code, serp: Search, hreflang: Languages,
  mobile: Smartphone, security: Shield, pagespeed: Zap, redirects: ArrowRight,
  content: BarChart3, technology: Cpu, accessibility: Accessibility, ux: MousePointerClick,
  cookies: Cookie, dns: Lock, 'broken-links': Link2, compare: GitCompareArrows,
  bulk: FileText, geo: Bot,
}

export default function ToolLandingPage() {
  const { slug } = useParams()
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const lang = i18n.language || 'cs'
  // URL slug může být lokalizovaný i starý český → kanonický český slug.
  const czSlug = slug ? resolveToolSlug(slug, lang) : null
  const base = czSlug ? getToolPage(czSlug) : undefined

  // Přesměruj staré / nekanonické URL na lokalizovanou (301-like, replace).
  // useLocation().pathname je už BEZ jazykového prefixu (řeší BrowserRouter basename),
  // stejně tak navigate() je relativní k basename → neprefixovat.
  useEffect(() => {
    if (!base || !czSlug) return
    const want = toolPath(czSlug, lang)
    if (location.pathname !== want) navigate(want, { replace: true })
  }, [base, czSlug, lang, location.pathname, navigate])

  if (!base) return <NotFoundPage />
  const tr = (TRANS[lang] || (lang !== 'cs' && TRANS.en) || []).find((x) => x.slug === base.slug)
  const page = (tr ? { ...base, ...tr } : base) as ToolPage
  return <ToolLanding page={page} key={page.slug + lang} />
}

function ToolLanding({ page }: { page: ToolPage }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const Icon = ICONS[page.toolId] || Wrench
  const [url, setUrl] = useState('')
  const [url2, setUrl2] = useState('')
  const [bulkUrls, setBulkUrls] = useState('')
  const [result, setResult] = useState<any>(null)

  const canonicalPath = toolPath(page.slug, i18n.language)
  // Meta keywords pro Seznam.cz — explicitní, nebo odvozené z H1 + obecné termíny (bez stuffingu)
  const keywords = page.keywords ?? `${page.h1.toLowerCase()}, ${page.toolId}, SEO nástroj, online, zdarma`

  // Hodnocení nástroje — pro AggregateRating schema (jen reálná hodnocení, count>0).
  const { data: rating } = useQuery<RatingSummary>({
    queryKey: ['tool-rating', page.toolId],
    queryFn: () => api.get(`/tools/${page.toolId}/rating`).then((r) => r.data),
    staleTime: 60_000,
  })
  const webAppSchema: any = {
    '@context': 'https://schema.org', '@type': 'WebApplication',
    name: page.h1, applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web', url: SITE + canonicalPath,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'CZK' },
  }
  if (rating && rating.count > 0) {
    webAppSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating.average,
      reviewCount: rating.count,
      bestRating: 5, worstRating: 1,
    }
  }

  useSeoHead({
    title: page.metaTitle,
    description: page.metaDesc,
    canonicalPath,
    hreflangPath: (l) => toolPath(page.slug, l),
    keywords,
    image: `${SITE}/og/nastroje-${page.slug}.png`,
    jsonLd: [
      {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: page.faq.map((f) => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      webAppSchema,
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Nástroje', item: SITE + '/tools' },
          { '@type': 'ListItem', position: 2, name: page.h1, item: SITE + canonicalPath },
        ],
      },
    ],
  })

  const run = useMutation({
    mutationFn: async () => {
      const payload: any = {}
      if (page.inputType === 'bulk') payload.urls = bulkUrls
      else { payload.url = url; if (page.inputType === 'compare') payload.url2 = url2 }
      const { data } = await api.post(`/tools/${page.toolId}`, payload)
      return data
    },
    onSuccess: (data) => setResult(data),
  })

  const canSubmit = page.inputType === 'bulk'
    ? bulkUrls.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).some(isValidUrl)
    : (isValidUrl(url) && (page.inputType !== 'compare' || isValidUrl(url2)))
  const findings = result ? extractFindings(result) : []
  const score = typeof result?.score === 'number' ? result.score : null

  const inp = 'w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm'

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-6">
        <Link to="/tools" className="hover:text-primary-500 transition">{t('nav.tools', { defaultValue: 'Nástroje' })}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-gray-600 dark:text-gray-300">{page.h1}</span>
      </nav>

      {/* Hero + nástroj */}
      <div className="flex items-start gap-4 mb-6">
        <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/25">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">{page.h1}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2 max-w-2xl">{page.intro}</p>
        </div>
      </div>

      {/* Vstup nástroje */}
      <div className="bg-white dark:bg-white/5 rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 p-6 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) run.mutate() }} className="space-y-3">
          {page.inputType === 'bulk' ? (
            <textarea value={bulkUrls} onChange={(e) => setBulkUrls(e.target.value)} rows={5}
              placeholder={'https://example.com/\nhttps://example.com/stranka'} className={inp} />
          ) : (
            <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com" className={inp}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- primary URL input of the tool, focusing it speeds up the main task
              autoFocus />
          )}
          {page.inputType === 'compare' && (
            <input type="text" value={url2} onChange={(e) => setUrl2(e.target.value)}
              placeholder="https://konkurence.cz" className={inp} />
          )}
          <button type="submit" disabled={!canSubmit || run.isPending}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition disabled:opacity-50 cursor-pointer">
            {run.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {t('toolpage.run', { defaultValue: 'Zkontrolovat zdarma' })}
          </button>
        </form>

        {/* Výsledek */}
        {result && (
          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-white/10">
            {result.error ? (
              <p className="text-red-500 text-sm">{result.message || result.error}</p>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-4">
                  {score !== null && <ScoreGauge score={score} size="sm" />}
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{t('toolpage.result', { defaultValue: 'Výsledek kontroly' })}</p>
                    <p className="text-sm text-gray-500">{findings.length} {t('toolpage.findings', { defaultValue: 'zjištění' })}</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {findings.slice(0, 8).map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      {f.status === 'pass' ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        : f.status === 'fail' ? <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />}
                      <span className="text-gray-700 dark:text-gray-300">
                        {f.label && <strong className="text-gray-900 dark:text-white">{f.label}: </strong>}
                        {f.message || f.recommendation}
                      </span>
                    </li>
                  ))}
                </ul>
                {page.inputType === 'url' && (
                  <button onClick={() => navigate(`/tools?tool=${page.toolId}&url=${encodeURIComponent(url)}`)}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-500 cursor-pointer">
                    {t('toolpage.full_result', { defaultValue: 'Zobrazit detailní výsledek a další nástroje' })}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Sdílení */}
      <ShareBar url={SITE + canonicalPath} title={page.metaTitle} className="mt-6" />

      {/* Co kontroluje */}
      <section className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('toolpage.what', { defaultValue: 'Co nástroj kontroluje' })}</h2>
          <ul className="space-y-2.5">
            {page.what.map((w, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                <CheckCircle className="w-4 h-4 text-primary-500 shrink-0 mt-0.5" />{w}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('toolpage.why', { defaultValue: 'Proč na tom záleží' })}</h2>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{page.why}</p>
          {page.kb && (
            <Link to={helpPath(page.kb, i18n.language)} className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-500">
              <BookOpen className="w-4 h-4" />{t('toolpage.learn_more', { defaultValue: 'Podrobný návod v nápovědě' })}
            </Link>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-10">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('toolpage.faq', { defaultValue: 'Časté dotazy' })}</h2>
        <div className="space-y-3">
          {page.faq.map((f, i) => (
            <details key={i} className="group bg-white dark:bg-white/5 rounded-xl border border-gray-100 dark:border-white/10 overflow-hidden">
              <summary className="font-semibold text-gray-900 dark:text-white text-sm list-none flex items-center justify-between gap-3 p-4 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-white/5 transition">
                <span>{f.q}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition shrink-0" />
              </summary>
              <p className="text-sm text-gray-600 dark:text-gray-300 px-4 pb-4 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Hodnocení nástroje uživateli */}
      <section className="mt-10">
        <ToolRating tool={page.toolId} />
      </section>

      {/* Související nástroje */}
      <section className="mt-10">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('toolpage.related', { defaultValue: 'Související nástroje' })}</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {page.related.map((slug) => {
            const r = TOOL_PAGES.find((x) => x.slug === slug)
            if (!r) return null
            const RIcon = ICONS[r.toolId] || Wrench
            return (
              <Link key={slug} to={toolPath(slug, i18n.language)}
                className="flex items-center gap-3 bg-white dark:bg-white/5 rounded-xl border border-gray-100 dark:border-white/10 p-4 hover:border-primary-300 hover:shadow-sm transition group">
                <RIcon className="w-5 h-5 text-primary-500 shrink-0" />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover:text-primary-600">{toolH1(slug, i18n.language)}</span>
              </Link>
            )
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-12 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-500 p-8 text-center text-white">
        <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-90" />
        <h2 className="text-2xl font-extrabold mb-2">{t('toolpage.cta_title', { defaultValue: 'Chcete kompletní SEO + GEO analýzu webu?' })}</h2>
        <p className="opacity-90 mb-5 max-w-xl mx-auto">{t('toolpage.cta_desc', { defaultValue: 'Spusťte všech 19 kontrol najednou, sledujte pozice na Googlu i Seznamu a získejte návrhy na zlepšení.' })}</p>
        <Link to="/" className="inline-flex items-center gap-2 bg-white text-primary-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition">
          {t('toolpage.cta_button', { defaultValue: 'Spustit kompletní analýzu' })}<ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  )
}
